import axios from "axios";
import { PDA_PREFIX } from "../../global/constants";
import { PREFERENCE_SCHEMA_VERSION } from "./preferenceKeys";

// SPEC-0061: one server-side store for every kept user preference, per tenant.
// A document is addressed by (scope, prefKey); the user always comes from the token.
// Shapes and key rules: see the SPEC-0060/0061 integration contract (backend
// `docs/user-preferences.md` once merged).
export const USER_PREFERENCES_PATH = `${PDA_PREFIX}/user-preferences`;
export {
  PREFERENCE_SCHEMA_VERSION,
  USER_SCOPE,
  portfolioScope,
  projectScope,
  PREF_KEY_PATTERN,
  DASHBOARD_KEY_PATTERN,
  OFFOLIO_STANDARD_DASHBOARD_KEY,
  DASHBOARD_TYPOLOGY,
  dashboardPrefKey,
  dashboardPointerPrefKey,
  ganttViewPrefKey,
  viewColumnsPrefKey,
  LOCAL_PREFS_IMPORT_FLAG_KEY,
} from "./preferenceKeys";

const docUrl = (scope, prefKey) => `${USER_PREFERENCES_PATH}/${scope}/${prefKey}`;

const ifMatch = (version) =>
  version == null ? {} : { headers: { "If-Match": String(version) } };

/**
 * @param {string[]} scopes e.g. ["user", "portfolio:12"]
 * @returns {Promise<Array<{scope:string, prefKey:string, published:boolean, schemaVersion:number, state:object, version:number, updatedAt:string, updatedBy:string|null}>>}
 */
/** A request on the session's critical path never hangs the shell: it fails and the defaults apply. */
export const REQUEST_TIMEOUT_MS = 15_000;

export const fetchPreferences = (scopes) =>
  axios
    .get(USER_PREFERENCES_PATH, {
      timeout: REQUEST_TIMEOUT_MS,
      params: { scope: scopes },
      paramsSerializer: (params) =>
        (params.scope || []).map((s) => `scope=${encodeURIComponent(s)}`).join("&"),
    })
    .then((res) => (Array.isArray(res.data) ? res.data : []));

/** @returns {Promise<object|null>} the caller's own document, or null (204) when none is saved. */
export const fetchPreference = (scope, prefKey) =>
  axios
    .get(docUrl(scope, prefKey), { timeout: REQUEST_TIMEOUT_MS })
    .then((res) => (res.status === 204 || !res.data ? null : res.data));

/**
 * Create or replace the caller's own document. Pass `version` to get a 409 when another tab wrote
 * meanwhile (optimistic concurrency); omit it for an unconditional write (one-time import).
 */
export const savePreference = (scope, prefKey, state, version) =>
  axios
    .put(docUrl(scope, prefKey), { schemaVersion: PREFERENCE_SCHEMA_VERSION, state }, { timeout: REQUEST_TIMEOUT_MS, ...ifMatch(version) })
    .then((res) => res.data);

export const deletePreference = (scope, prefKey) => axios.delete(docUrl(scope, prefKey), { timeout: REQUEST_TIMEOUT_MS });

/** Publishers only (SET-06). Creates or replaces the published row of the scope; version + 1. */
export const savePublishedPreference = (scope, prefKey, state, version) =>
  axios
    .put(`${docUrl(scope, prefKey)}/published`, { schemaVersion: PREFERENCE_SCHEMA_VERSION, state }, ifMatch(version))
    .then((res) => res.data);

export const deletePublishedPreference = (scope, prefKey) => axios.delete(`${docUrl(scope, prefKey)}/published`);

/** @returns {Promise<number>} how many users follow ("Use as is") the published dashboard. */
export const fetchPublishedFollowers = (scope, prefKey) =>
  axios.get(`${docUrl(scope, prefKey)}/published/followers`).then((res) => Number(res.data?.count) || 0);

export const isStaleWriteError = (error) => error?.response?.status === 409;
export const isTooLargeError = (error) => error?.response?.status === 413;
